import React, { MouseEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { FormattedMessage, useIntl } from 'react-intl';
import dayjs from 'dayjs';
import { useApolloClient, useQuery } from '@apollo/client';
import { usePagination, useSortBy, useTable } from 'react-table';
import { Button, Modal } from 'react-bootstrap';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import { getDocumentTemplates, getOrganizationsTemplatesByUser, getTemplateFolders } from '../../api/queries_v2';
import { fillPlaceholders, paths, useNavigate } from '../../routing';
import slugify from '../../utils/slugify';
import { useTeamInfo } from '../../team-id-context';
import { usePermissions } from '../../auth';
import { useUserRestrictions } from '../../auth/user-restrictions';
import { Dropdown, DropdownBody, DropdownItem, DropdownToggle } from '../../components/dropdown';
import SearchInput from '../../components/search-input';
import { ContentHeader } from '../../components/private-layout';
import { Table } from '../../components/table';
import { Checkbox } from '../../components/checkbox-or-radio-field';
import LoadingSpinner from '../../components/loading-spinner';
import { TemplateStatus } from '../templates/templateConstants';
import AddNewTemplateFormik from '../../forms/AddNewTemplateFormContainer';
import { useDocumentTeamPermissions } from '../document-wizard/shared/hooks/use-document-team-permissions/use-document-team-permissions';
import { Permissions } from '../../shared/constants/permissions';
import { TemplateType } from '../../consts/forms-types';
import { ModalActionsEnum } from '../../components/document-table/DocumentTableConstants';
import { FileUploadModal } from '../../components/shared/modals/fileUploadModal';
import { useModals } from '../../contexts/ModalsContext';
import SubscriptionRequiredModal from '../modals/subscriptionModal/SubscriptionRequiredModal';
import Tooltip from '../../components/tooltip';
import { fuzzyInputSearch } from '../deals/helpers/determineDealsActionModalMessage';
import { TitleFormattedMessage } from '../../components/titleFormattedMessage';
import PropdocsModal from '../../components/modal/PropdocsModal';
import styles from '../templates/styles.module.scss';
import { ContentBody } from '../../components/private-layout/ContentBody';
import { ReactComponent as FilterIcon } from '../../shared/icons/filter-icon.svg';
import { ReactComponent as PlusIcon } from '../../shared/icons/plus-icon.svg';
import { ReactComponent as HomeIcon } from '../../shared/icons/home-icon.svg';
import { ReactComponent as FolderIcon } from '../../shared/icons/folder-icon.svg';
import { ReactComponent as GlobeIcon } from '../../shared/icons/globe-icon.svg';
import { MobileTable } from '../../components/mobile-layout/MobileTable';
import { MobileTemplateItem } from '../../components/mobile-layout/MobileTemplateItem';
import { MobilePagination } from '../../components/mobile-layout/MobilePagination';
import { templateStatusMessages, statusOptions } from './constants';
import FilterSidebar from './components/filterSidebar';
import { CreateClauseFolderModal } from './components/CreateClauseFolderModal';
import ClauseFolderControls from './components/ClauseFolderControls';
import ClauseControls from './components/ClauseControls';
import { DropdownProvider } from '../../contexts/DropdownContext';

enum AddTemplateActionStatus {
  CLAUSE = 'Clause',
}

type ColumnsCellType = {
  cell: {
    value: string;
    row: {
      values: {
        template: {
          id: string;
          organizationName: string;
          fromOrganization?: boolean;
          isFolder?: boolean;
          sharedFromTeam?: { id: string; name: string };
          sharedToAffiliatedWorkspaces?: boolean;
          description?: string;
        };
        status?: string;
        templateType?: TemplateType;
      };
    };
  };
};

const defaultTypeOptions = new Set<string>([TemplateType.CLAUSE]);
const defaultTemplateStatusOptions = new Set<string>([TemplateStatus.ACTIVE]);

// Custom sort functions that prioritize folders first
const createFolderPrioritySortFn = (sortFn: (a: any, b: any) => number) => {
  return (a: any, b: any) => {
    // Always show folders first, regardless of sorting
    if (a.original.type === 'folder' && b.original.type === 'clause') return -1;
    if (a.original.type === 'clause' && b.original.type === 'folder') return 1;

    // If both are the same type, apply the provided sorting function
    return sortFn(a, b);
  };
};

const nameSortFn = (a: any, b: any) => {
  const nameA = a.values.name || '';
  const nameB = b.values.name || '';
  return nameA.localeCompare(nameB);
};
const statusSortFn = (a: any, b: any) => {
  const statusA = a.values.status || '';
  const statusB = b.values.status || '';
  return statusA.localeCompare(statusB);
};
const dateSortFn = (a: any, b: any) => {
  if (!a.values.updatedAt || !b.values.updatedAt) return 0;
  return new Date(b.values.updatedAt).getTime() - new Date(a.values.updatedAt).getTime();
};

const ClausesLandingPage = () => {
  const { activeModal, setActiveModal } = useModals();
  const { formatMessage } = useIntl();
  const { teamId, teamName } = useTeamInfo();
  const navigate = useNavigate();
  const { restrictionsLoading, hasAccessToClauses, canCreateClauses } = useUserRestrictions();
  const { isAdmin, hasPermission } = usePermissions() as {
    isAdmin: boolean;
    hasPermission: (permission: string) => boolean;
  };
  const apolloClient = useApolloClient();
  const [showAddTemplate, setShowAddTemplate] = useState(false);
  const [showEditClause, setShowEditClause] = useState(false);
  const [selectedTemplateType, setSelectedTemplateType] = useState<TemplateType | null>(null);
  const [filterText, setFilterText] = useState('');
  const [selectedCreator, setSelectedCreator] = useState<string>('');
  const [showCreatorDropdown, setShowCreatorDropdown] = useState(false);
  const [filterStatuses, setFilterStatuses] = React.useState(new Set(defaultTemplateStatusOptions));
  const [filterTemplateTypes] = React.useState(new Set(defaultTypeOptions));
  const [showFilter, setShowFilter] = useState(false);
  const [sorting, setSorting] = useState('');
  const [showCreateFolderModal, setShowCreateFolderModal] = useState(false);
  const [currentFolderId, setCurrentFolderId] = useState<string | null>(null);
  const [currentVirtualFolderId, setCurrentVirtualFolderId] = useState<string | null>(null);
  const [folderPath, setFolderPath] = useState<Array<{ id: string | null; name: string; isVirtual?: boolean }>>([
    { id: null, name: 'Clauses' },
  ]);
  const [selectedFolderForCreation, setSelectedFolderForCreation] = useState<string | null>(null);
  const canEdit = hasPermission(Permissions.Edit);
  const canManageTemplates = hasPermission(Permissions.ManageTemplates);
  const isTemplateEditable = isAdmin || canEdit || canManageTemplates;
  const canManageClauses = isAdmin || canManageTemplates; // Only admin or manage_templates can create/manage

  const [isSubscriptionModalActive, setSubscriptionModalActive] = useState(false);

  const {
    loading: isDocumentTemplatesDataLoading,
    data: documentTemplatesData,
    refetch,
  } = useQuery(getDocumentTemplates as any, {
    fetchPolicy: 'network-only',
    skip: !teamId || !hasAccessToClauses,
    variables: {
      teamId,
      templateType: 'clause',
    },
  });

  const { loading: isUserOrgsTemplatesDataLoading, data: userOrgsTemplatesData } = useQuery(
    getOrganizationsTemplatesByUser,
    {
      fetchPolicy: 'network-only',
      skip: !teamId || !hasAccessToClauses,
      variables: {
        teamId,
        templateType: 'clause',
      },
    }
  );

  // Removed workspaceSharedTemplatesByUser query - getDocumentTemplates already includes shared templates

  const {
    loading: isTemplateFoldersLoading,
    data: templateFoldersData,
    refetch: refetchFolders,
  } = useQuery(getTemplateFolders as any, {
    fetchPolicy: 'network-only',
    skip: !teamId,
    variables: {
      teamId,
      folderType: 'clause',
    },
  });

  const allTemplates = useMemo(() => {
    if (isDocumentTemplatesDataLoading || isUserOrgsTemplatesDataLoading || !documentTemplatesData) {
      return [];
    }

    const userTemplatesIds = documentTemplatesData?.getDocumentTemplates.map((template: any) => template.id);
    const organizationTemplates =
      userOrgsTemplatesData?.organizationsTemplatesByUser
        .filter((orgTemplate: any) => !userTemplatesIds?.includes(orgTemplate.id))
        .map((orgTemplate: any) => {
          return { ...orgTemplate, fromOrganization: true };
        }) ?? [];

    // Matches templates page exactly - getDocumentTemplates already includes shared templates
    return [...(documentTemplatesData?.getDocumentTemplates ?? []), ...organizationTemplates];
  }, [isDocumentTemplatesDataLoading, documentTemplatesData, isUserOrgsTemplatesDataLoading, userOrgsTemplatesData]);

  const allCreators = useMemo(() => {
    const creators = new Set<string>();

    // Always add "Your Team" as an option, even if there are no templates yet
    if (teamName) {
      creators.add(`${teamName} (Your Team)`);
    }

    // Only process templates if they exist
    if (allTemplates && allTemplates.length > 0) {
      allTemplates.forEach((template: any) => {
        // Add regular team names
        if (template?.team?.name) {
          if (template?.team?.id === teamId) {
            // Already added above, skip to avoid duplicate
            if (!creators.has(`${template.team.name} (Your Team)`)) {
              creators.add(`${template.team.name} (Your Team)`);
            }
          } else {
            creators.add(template.team.name);
          }
        }
        // Add shared template source teams
        if (template?.sharedFromTeam?.name) {
          creators.add(template.sharedFromTeam.name);
        }
      });
    }

    return Array.from(creators).sort((a: any, b: any) => {
      // Keep "Your Team" at the top
      if (a.includes('(Your Team)')) return -1;
      if (b.includes('(Your Team)')) return 1;
      return a.localeCompare(b);
    });
  }, [allTemplates, teamId, teamName]);

  // Set default creator to match templates behavior
  useEffect(() => {
    // Set default to "Your Team" if available, otherwise first creator
    const yourTeamCreator = allCreators.find((creator) => creator.includes('(Your Team)'));
    const defaultCreator = yourTeamCreator || allCreators[0] || '';
    if (!selectedCreator && defaultCreator) {
      setSelectedCreator(defaultCreator);
    }
  }, [allCreators, selectedCreator]);

  // Determine if selected creator is user's own team and what permissions apply
  const { selectedCreatorIsOwnTeam, canEditInSelectedContext } = useMemo(() => {
    const isOwnTeam =
      selectedCreator?.includes('(Your Team)') ||
      (!selectedCreator && allCreators.length > 0 && allCreators.find((c) => c.includes('(Your Team)')));

    return {
      selectedCreatorIsOwnTeam: isOwnTeam,
      canEditInSelectedContext: isOwnTeam && isTemplateEditable,
    };
  }, [selectedCreator, allCreators, isTemplateEditable]);

  // Reset folder navigation when creator changes
  useEffect(() => {
    setCurrentFolderId(null);
    setCurrentVirtualFolderId(null);
    setFolderPath([{ id: null, name: 'Clauses' }]);
  }, [selectedCreator]);

  const clauseIsUpdated = useCallback(() => {
    setShowAddTemplate(false);
    refetch();
    refetchFolders();
  }, [refetch, refetchFolders]);

  const onAddClauseModalHide = () => {
    setShowAddTemplate(false);
    setSelectedTemplateType(null);
  };

  const onAddClauseShowModal = (templateType: TemplateType) => {
    setShowAddTemplate(true);
    setSelectedTemplateType(templateType);
  };

  const permissions = useDocumentTeamPermissions({ apolloClient, teamId });

  const columns = useMemo(() => {
    const baseColumns = [
      {
        Header: <FormattedMessage id="title.deals.title" defaultMessage="Clause Name" />,
        accessor: 'name',
        sortType: createFolderPrioritySortFn(nameSortFn),
        Cell: ({
          cell: {
            value,
            row: {
              values: { template },
              original,
            },
          },
        }: ColumnsCellType & { cell: { row: { original: any } } }) => {
          return (
            <div className={styles.headerTitleTableColumn}>
              {template.isFolder && (
                <FolderIcon
                  className={styles.folderIcon}
                  style={{ marginRight: '8px', width: '16px', height: '16px', fill: '#006c90' }}
                />
              )}
              {filterText && original.folderPath ? (
                <div>
                  <FormattedMessage id="cta.go-to.details" defaultMessage={value} />
                  <div style={{ fontSize: '12px', color: '#6c757d', marginTop: '2px' }}>{original.folderPath}</div>
                </div>
              ) : (
                <FormattedMessage id="cta.go-to.details" defaultMessage={value} />
              )}
            </div>
          );
        },
      },
    ];

    // Conditionally add Creator column when searching
    if (filterText) {
      baseColumns.push({
        Header: <FormattedMessage id="title.deals.template-creator" defaultMessage="Creator" />,
        accessor: 'templateCreator',
        sortType: createFolderPrioritySortFn((a: any, b: any) => a.localeCompare(b)),
        Cell: ({ cell: { value } }: ColumnsCellType) => {
          return <span>{value}</span>;
        },
      });
    }

    // Add remaining columns
    baseColumns.push(
      {
        Header: <FormattedMessage id="title.deals.clause-status" defaultMessage="Status" />,
        accessor: 'status',
        sortType: createFolderPrioritySortFn(statusSortFn),
        Cell: ({
          cell: {
            row: {
              values: { status, template },
            },
          },
        }: ColumnsCellType) => {
          const selectedClause = documentTemplatesData?.getDocumentTemplates?.find((c: any) => c.id === template.id);

          return (
            <span className={styles.templateStatus}>
              <FormattedMessage id="phrase.clause-status-readout" defaultMessage={status} values={{ status }} />
              {selectedClause?.sharedToAffiliatedWorkspaces && !template.sharedFromTeam && (
                <Tooltip
                  placement="bottom"
                  testId="workspace-share-tooltip"
                  icon={<GlobeIcon className="text-primary ml-2" style={{ width: '16px', height: '16px' }} />}
                >
                  Shared to other workspaces
                </Tooltip>
              )}
            </span>
          );
        },
      },
      {
        Header: <FormattedMessage id="title.clauses.last-updated" defaultMessage="Last Updated" />,
        accessor: 'updatedAt',
        sortType: createFolderPrioritySortFn(dateSortFn),
        Cell: ({ cell: { value } }: any) => {
          return <span className="text-nowrap">{value && dayjs(value).format('LL')}</span>;
        },
      },
      {
        Header: (
          <span className="sr-only">
            <FormattedMessage id="title.deals.detailsLink" defaultMessage="Link to Deal Details" />
          </span>
        ),
        accessor: 'template',
        sortType: (a: any, b: any) => 0, // No sorting for action column
        Cell: ({
          cell: {
            value,
            row: {
              values: { status, template, templateType },
              original,
            },
          },
        }: ColumnsCellType & { cell: { row: { original: any } } }) => {
          // Find the clause data from the appropriate source
          let selectedClausesData;
          if (template.fromOrganization) {
            // Clause is from an organization
            selectedClausesData = {
              ...userOrgsTemplatesData?.organizationsTemplatesByUser.find(
                (fetchedClause: any) => fetchedClause.id === template.id
              ),
              fromOrganization: true,
            };
          } else {
            // Clause is from the team (including shared ones - getDocumentTemplates includes both)
            selectedClausesData = documentTemplatesData!.getDocumentTemplates.find(
              (fetchedClause: any) => fetchedClause.id === template.id
            );
          }

          return (
            <div
              onClick={(evt: MouseEvent) => {
                evt.stopPropagation();
              }}
              className={styles.actionsTableColumn}
            >
              {template.isFolder ? (
                // Folder actions - different from clause actions
                <ClauseFolderControls
                  folderId={template.id}
                  folderName={original.name || 'Unknown Folder'}
                  editPermission={isTemplateEditable}
                  managePermission={isAdmin || canManageTemplates}
                  teamId={teamId}
                  onSuccess={() => {
                    clauseIsUpdated();
                    refetchFolders();
                  }}
                />
              ) : !template.fromOrganization && !template.sharedFromTeam && selectedCreatorIsOwnTeam ? (
                <ClauseControls
                  status={status || ''}
                  editPermission={canEditInSelectedContext}
                  managePermission={canEditInSelectedContext && (isAdmin || canManageTemplates)}
                  selectedTemplateData={selectedClausesData}
                  teamId={teamId}
                  onSuccess={clauseIsUpdated}
                  templateType={TemplateType.CLAUSE}
                />
              ) : template.sharedFromTeam ? (
                <div className={styles.templatesTooltipContainer}>
                  <Tooltip placement="bottom" testId="shared-clause-tooltip">
                    This Clause is shared from {template.sharedFromTeam.name} workspace and cannot be edited here.
                  </Tooltip>
                </div>
              ) : (
                <div className={styles.templatesTooltipContainer}>
                  <Tooltip placement="bottom" testId="organization-template-tooltip">
                    This Clause is available due to your active membership in {template.organizationName}, it cannot be
                    edited.
                  </Tooltip>
                </div>
              )}
            </div>
          );
        },
      }
    );

    return baseColumns;
  }, [
    clauseIsUpdated,
    canEdit,
    isAdmin,
    isTemplateEditable,
    canEditInSelectedContext,
    selectedCreatorIsOwnTeam,
    documentTemplatesData,
    teamId,
    permissions,
    userOrgsTemplatesData,
    filterText,
  ]);

  const clauseActions = {
    buttonText: 'Add New Clause',
    text: AddTemplateActionStatus.CLAUSE,
    onClick: () => (canCreateClauses ? onAddClauseShowModal(TemplateType.CLAUSE) : setSubscriptionModalActive(true)),
    isAvailable: canManageClauses,
  };

  const onClauseDetail = (clause: any) => {
    // If it's a folder, navigate into the folder instead of opening a clause
    if (clause.isFolder) {
      const newPath = [...folderPath, { id: clause.id, name: clause.name || 'Folder' }];
      setFolderPath(newPath);
      setCurrentFolderId(clause.id);
      return;
    }

    // Otherwise, navigate to the clause document
    const templateUrl = fillPlaceholders(
      !clause.fromOrganization ? paths.templates.document : paths.templates.orgTemplate,
      {
        templateId: clause.id,
        teamId,
      }
    );

    navigate(templateUrl);
  };

  const teamClausesData = useMemo(() => {
    // First pass: determine which clauses should be shown (for folder inclusion logic)
    const clausesPassingCreatorFilter = allTemplates.filter((clause: any) => {
      // Handle creator filtering based on selected creator
      if (selectedCreator) {
        if (selectedCreatorIsOwnTeam) {
          // When viewing "Your Team", show our own clauses (excluding shared ones)
          const isOwnClause = clause.team?.id === teamId && !clause.sharedFromTeam;
          return isOwnClause;
        } else {
          // When viewing other creators, show clauses from that creator
          const isSharedFromSelectedCreator = clause.sharedFromTeam?.name === selectedCreator;
          const isFromSelectedOrganization = clause.fromOrganization && clause.team?.name === selectedCreator;
          const isFromSelectedTeamDirect = clause.team?.name === selectedCreator && !clause.sharedFromTeam;

          return isSharedFromSelectedCreator || isFromSelectedOrganization || isFromSelectedTeamDirect;
        }
      }
      return true;
    });

    // Second pass: full clause filtering including folder logic
    const filteredClauses =
      allTemplates
        .filter((clause: any) => {
          // When not searching, apply folder filtering
          if (!filterText) {
            if (selectedCreatorIsOwnTeam) {
              // Filter by current folder when viewing own team
              const clauseFolderId = clause.folder?.id || null;
              if (clauseFolderId !== currentFolderId) {
                return false;
              }
            } else if (selectedCreator && !selectedCreatorIsOwnTeam) {
              // When viewing other creators, handle virtual folder navigation
              if (currentVirtualFolderId) {
                // If viewing inside a virtual folder, only show clauses from that folder
                const clauseVirtualFolderId = clause.folder?.id ? `virtual-${clause.folder.id}` : null;
                if (clauseVirtualFolderId !== currentVirtualFolderId) {
                  return false;
                }
              } else {
                // If at root level, hide clauses that belong to folders (they'll be shown via folder navigation)
                if (clause.folder?.id) {
                  return false;
                }
              }
            }
          }
          // When searching, show all matching clauses regardless of folder

          const templateType = clause.templateType;

          const hasClauseActualStatus = filterStatuses.has(clause.status);
          const hasClauseActualType = filterTemplateTypes.has(templateType);

          const hasClauseFilteredStatus = filterStatuses.size && filterStatuses.size < statusOptions.length;
          const hasClauseFilteredType = filterTemplateTypes.size && filterTemplateTypes.size < defaultTypeOptions.size;

          // Filter by selected creator
          if (selectedCreator) {
            if (selectedCreatorIsOwnTeam) {
              // Show clauses from user's own team (including cloned ones)
              const isOwnTemplate = clause.team?.id === teamId && !clause.sharedFromTeam;
              if (!isOwnTemplate) {
                return false;
              }
            } else {
              // Show clauses from selected external creator
              const isSharedFromSelectedCreator = clause.sharedFromTeam?.name === selectedCreator;
              const isFromSelectedOrganization = clause.fromOrganization && clause.team?.name === selectedCreator;
              const isFromSelectedTeamDirect = clause.team?.name === selectedCreator && !clause.sharedFromTeam;

              if (!isSharedFromSelectedCreator && !isFromSelectedOrganization && !isFromSelectedTeamDirect) {
                return false;
              }
            }
          }
          // When no creator is selected, show all clauses (owned, cloned, and shared)

          return !(
            (filterText && !fuzzyInputSearch({ filteredText: filterText, searchValue: clause.name })) ||
            (hasClauseFilteredStatus && !hasClauseActualStatus) ||
            (hasClauseFilteredType && !hasClauseActualType)
          );
        })
        .map((clause: any) => {
          const templateType = clause.templateType;

          return {
            type: 'clause',
            template: {
              id: clause.id,
              fromOrganization: clause.fromOrganization,
              organizationName: clause.team?.organization?.name,
              description: clause.description || '',
              sharedFromTeam: clause.sharedFromTeam,
              sharedToAffiliatedWorkspaces: clause.sharedToAffiliatedWorkspaces,
            },
            name: formatMessage({
              id: 'format.name',
              defaultMessage: clause.name,
            }),
            status: clause.hidden ? 'inactive' : 'active',
            updatedAt: clause.updatedAt,
            templateType: templateType,
            templateCreator: clause.sharedFromTeam ? clause.sharedFromTeam.name : clause.team?.name || '',
            folderPath:
              clause.folder?.path || clause.folder?.name
                ? `Clauses > ${Array.isArray(clause.folder.path) ? clause.folder.path.join(' > ') : (clause.folder.path || clause.folder.name)}`
                : null,

            onRowClick: () => {
              const templateUrl = fillPlaceholders(
                !clause.fromOrganization ? paths.templates.document : paths.templates.orgTemplate,
                {
                  templateId: clause.id,
                  teamId,
                }
              );

              navigate(templateUrl);
            },
          };
        }) ?? [];

    // Process folders - only show when not searching
    let filteredFolders: any[] = [];

    // Don't show folders when searching
    if (filterText) {
      filteredFolders = [];
    } else if (selectedCreatorIsOwnTeam) {
      // Show regular folders from database when viewing own team
      filteredFolders =
        templateFoldersData?.getTemplateFolders
          ?.filter((folder: any) => {
            // Filter by current folder
            const folderParentId = folder.parentFolder?.id || null;
            if (folderParentId !== currentFolderId) {
              return false;
            }
            return true;
          })
          .map((folder: any) => ({
            type: 'folder',
            template: {
              id: folder.id,
              isFolder: true,
            },
            name: folder.name,
            status: 'active',
            updatedAt: folder.updatedAt,
            templateType: 'Folder',
            templateCreator: teamName || 'Your Team',
            folderPath: folder.path ? `Clauses > ${Array.isArray(folder.path) ? folder.path.join(' > ') : folder.path}` : null,
            onRowClick: () => {
              // Navigate to folder
              const newPath = [...folderPath, { id: folder.id, name: folder.name }];
              setFolderPath(newPath);
              setCurrentFolderId(folder.id);
            },
          })) ?? [];
    } else if (selectedCreator && !selectedCreatorIsOwnTeam && !filterText) {
      // When viewing other creators, create virtual folders from clause folder paths (but not when searching)
      if (!currentVirtualFolderId) {
        // Only show virtual folders at root level (not when inside a virtual folder)
        const folderMap = new Map<string, { name: string; clauses: any[] }>();

        clausesPassingCreatorFilter.forEach((clause) => {
          if (clause.folder) {
            const folderId = `virtual-${clause.folder.id}`;
            const folderName = clause.folder.name || clause.folder.path;

            if (!folderMap.has(folderId)) {
              folderMap.set(folderId, { name: folderName, clauses: [] });
            }
            folderMap.get(folderId)!.clauses.push(clause);
          }
        });

        // Create virtual folder items
        filteredFolders = Array.from(folderMap.entries()).map(([folderId, folderData]) => ({
          type: 'folder',
          template: {
            id: folderId,
            isFolder: true,
            isVirtualFolder: true,
          },
          name: folderData.name,
          status: 'active',
          updatedAt: new Date().toISOString(),
          templateType: 'Folder',
          templateCreator: selectedCreator,
          folderPath: null,
          onRowClick: () => {
            // Navigate to virtual folder
            const newPath = [...folderPath, { id: folderId, name: folderData.name, isVirtual: true }];
            setFolderPath(newPath);
            setCurrentVirtualFolderId(folderId);
          },
        }));
      }
    }

    // Combine folders and clauses (sorting will be handled by react-table with folder priority)
    return [...filteredFolders, ...filteredClauses];
  }, [
    allTemplates,
    templateFoldersData,
    formatMessage,
    filterText,
    filterStatuses,
    filterTemplateTypes,
    allCreators.length,
    teamId,
    sorting,
    navigate,
    currentFolderId,
    folderPath,
    selectedCreator,
    selectedCreatorIsOwnTeam,
  ]);

  // All folders for creation modal folder selector
  const allFoldersForCreation = templateFoldersData?.getTemplateFolders ?? [];

  const sortBy = useMemo(() => {
    switch (sorting) {
      case 'ascName':
        return {
          id: 'name',
          desc: false,
        };
      case 'descName':
        return {
          id: 'name',
          desc: true,
        };
      case 'statusActive':
        return {
          id: 'status',
          desc: false,
        };
      case 'statusArchived':
        return {
          id: 'status',
          desc: true,
        };
      case 'dateToLatest':
        return {
          id: 'updatedAt',
          desc: false,
        };
      case 'dateToNewest':
        return {
          id: 'updatedAt',
          desc: true,
        };

      default:
        return {
          id: 'updatedAt',
          desc: false,
        };
    }
  }, [sorting]);

  const table = useTable(
    {
      data: teamClausesData,
      //@ts-ignore
      columns,
      initialState: {
        sortBy: [sortBy],
        pageSize: 50,
        pageIndex: 0,
      },
      autoResetPage: false,
    } as any,
    useSortBy,
    usePagination
  );

  // Redirect free users to deals page with upgrade modal
  useEffect(() => {
    if (!restrictionsLoading && !hasAccessToClauses) {
      navigate(fillPlaceholders(paths.deals.landing, { teamId }) + '?upgrade=clauses');
    }
  }, [restrictionsLoading, hasAccessToClauses, navigate, teamId]);

  // Early return to prevent rendering for users without access
  if (!restrictionsLoading && !hasAccessToClauses) {
    return null;
  }

  if (isDocumentTemplatesDataLoading || isUserOrgsTemplatesDataLoading) {
    return <LoadingSpinner />;
  }

  return (
    <DropdownProvider>
      <Helmet title={formatMessage({ id: 'title.clauses', defaultMessage: 'Clauses' })} />
      {isSubscriptionModalActive && (
        <SubscriptionRequiredModal
          isOpen={isSubscriptionModalActive}
          onClose={() => {
            setSubscriptionModalActive(false);
          }}
        />
      )}
      <ContentHeader>
        <Row>
          <Col lg={6} className={'ml-3 mr-3'}>
            <div className={styles.title} data-testid="deals-header" role={'heading'}>
              <FormattedMessage id="title.clauses" defaultMessage="Clauses" />
            </div>
          </Col>
        </Row>
      </ContentHeader>

      <ContentBody className={styles.contentBodyFull}>
        {/* Desktop Search/Filters */}
        <div className="mb-3 non-mobile mx-3">
          <div className="d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center" style={{ gap: '12px' }}>
              <SearchInput
                className={styles.searchInboxBox}
                value={filterText}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => setFilterText(event.currentTarget.value)}
                placeholder={formatMessage({
                  id: 'deals.filter.title.placeholder',
                  defaultMessage: 'Search clause name',
                })}
                aria-label={formatMessage({
                  id: 'deals.filter.title.label',
                  defaultMessage: 'Name',
                })}
              />

              {/* Org Selector */}
              <div className={styles.creatorDropdownContainer}>
                <Dropdown show={showCreatorDropdown} onToggle={setShowCreatorDropdown}>
                  <Dropdown.Toggle
                    id="dropdown-filter-creator"
                    as={DropdownToggle}
                    className={styles.creatorDropdown}
                  >
                    <span className={styles.creatorText} title={selectedCreator}>
                      <strong>From:</strong> {selectedCreator || 'Select Org'}
                    </span>
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    <DropdownBody>
                      {allCreators.map((creator: string) => (
                        <Dropdown.Item
                          key={creator}
                          as={Button}
                          variant="link"
                          onClick={() => {
                            setSelectedCreator(creator);
                            setShowCreatorDropdown(false);
                          }}
                          className={selectedCreator === creator ? 'font-weight-bold' : ''}
                        >
                          {creator}
                        </Dropdown.Item>
                      ))}
                    </DropdownBody>
                  </Dropdown.Menu>
                </Dropdown>
              </div>

              <div className={styles.combinedFilterContainer}>
                <Dropdown>
                  <Dropdown.Toggle id="dropdown-filter-status" as={DropdownToggle}>
                    <FormattedMessage
                      id="deals.filter.status.label-readout"
                      defaultMessage={`{filterCount, select,
                      0 {Status (any)}
                      1 {Status ({status, select,
                        active {Active}
                        draft {Draft}
                        other {{status}}
                      })}
                      other {{filterCount} statuses}
                    }`}
                      values={{
                        filterCount: filterStatuses.size === statusOptions.length ? 0 : filterStatuses.size,
                        status: filterStatuses.size ? filterStatuses.values().next().value : '',
                      }}
                    />
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    <DropdownItem highlightable>
                      <Checkbox
                        checked={filterStatuses.size === statusOptions.length}
                        indeterminate={filterStatuses.size > 0 && filterStatuses.size < statusOptions.length}
                        id="dropdown-filter-statuses-_all"
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                          const checkbox = event.currentTarget;
                          if (checkbox.checked) {
                            setFilterStatuses(new Set(Object.keys(templateStatusMessages)));
                          } else {
                            setFilterStatuses(new Set());
                          }
                        }}
                        label={formatMessage({
                          id: 'deals.filter.status.all-statuses',
                          defaultMessage: 'Any status',
                        })}
                      />
                    </DropdownItem>
                    <DropdownBody>
                      {statusOptions.map(({ value: status, label }) => (
                        <DropdownItem key={status} highlightable>
                          <Checkbox
                            key={status}
                            value={status}
                            checked={filterStatuses.has(status)}
                            id={`dropdown-filter-statuses-${slugify(status)}`}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                              const checkbox = event.currentTarget;
                              const newSet = new Set(filterStatuses);
                              if (checkbox.checked) newSet.add(checkbox.value);
                              else newSet.delete(checkbox.value);
                              setFilterStatuses(newSet);
                            }}
                            label={<FormattedMessage {...label} />}
                          />
                        </DropdownItem>
                      ))}
                    </DropdownBody>
                  </Dropdown.Menu>
                </Dropdown>
              </div>
            </div>

            <div className={styles.actionsDropdownContainer}>
              {clauseActions.isAvailable && (
              <>
                {canCreateClauses ? (
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <Button variant="primary" onClick={() => setShowCreateFolderModal(true)}>
                      <FormattedMessage id="cta.new-folder" defaultMessage="New Folder" />
                    </Button>
                    <Button variant="secondary" onClick={clauseActions.onClick}>
                      <FormattedMessage id="cta.new-clause" defaultMessage="New Clause" />
                    </Button>
                  </div>
                ) : (
                  <Button data-testid="createClause" variant="secondary" onClick={clauseActions.onClick}>
                    <TitleFormattedMessage id="cta.create-clause" defaultMessage={clauseActions.buttonText} />
                  </Button>
                )}
                <PropdocsModal
                  show={showAddTemplate}
                  setShow={onAddClauseModalHide}
                  size="lg"
                  title={
                    <FormattedMessage
                      id="title.add-team-member"
                      data-testid="createClauseModalHeader"
                      defaultMessage={`Create ${AddTemplateActionStatus.CLAUSE}`}
                    />
                  }
                >
                  <AddNewTemplateFormik
                    bodyContainer={({ children }: { children: React.ReactNode }) => <div>{children}</div>}
                    actionsContainer={({ children }: { children: React.ReactNode }) => (
                      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem', marginTop: '1rem' }}>
                        {children}
                      </div>
                    )}
                    renderSecondaryAction={() => (
                      <Button variant="link" className="text-secondary" onClick={onAddClauseModalHide}>
                        <TitleFormattedMessage id="cta.cancel" defaultMessage="Cancel" />
                      </Button>
                    )}
                    onSuccess={clauseIsUpdated}
                    templateFormEditMode={false}
                    templateType={selectedTemplateType}
                    folders={allFoldersForCreation}
                    selectedFolderId={selectedFolderForCreation}
                    onFolderSelectionChange={setSelectedFolderForCreation}
                    foldersLoading={isTemplateFoldersLoading}
                  />
                </PropdocsModal>
                <Modal show={showEditClause} onHide={() => setShowEditClause(false)} size="sm">
                  <div className={styles.templateModal}>
                    <Modal.Header closeButton closeLabel={formatMessage({ id: 'cta.close', defaultMessage: 'Close' })}>
                      <Modal.Title>
                        <header>
                          <FormattedMessage id="title.add-team-member" defaultMessage="Edit Clause" />
                        </header>
                      </Modal.Title>
                    </Modal.Header>
                  </div>
                </Modal>
              </>
            )}
            </div>
          </div>
        </div>

        {/* Mobile Search/Filters */}
        <Row className="mobile">
          <Col className="d-flex align-items-center" style={{ padding: '16px', gap: '16px' }}>
            <div className={'d-flex flex-fill'}>
              <SearchInput
                iconClassName="mobileSearch__icon"
                inputClassName="mobileSearch"
                className="mobileSearch__container"
                value={filterText}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => setFilterText(event.currentTarget.value)}
                placeholder={formatMessage({
                  id: 'deals.filter.title.placeholder',
                  defaultMessage: 'Search clause name',
                })}
                aria-label={formatMessage({
                  id: 'deals.filter.title.label',
                  defaultMessage: 'Name',
                })}
              />

              <Button className="filterBtn" onClick={() => setShowFilter(true)} data-testid="mobileFilterBtn">
                <FilterIcon width={24} height={24} />
              </Button>

              {isTemplateEditable && clauseActions.isAvailable && (
                <Button
                  className="addBtn"
                  onClick={clauseActions.onClick}
                  style={{ marginLeft: '0.5rem' }}
                >
                  <PlusIcon width={24} height={24} />
                </Button>
              )}
            </div>
          </Col>
        </Row>

        {/* Breadcrumb Navigation */}
        <div className="mb-2 non-mobile mx-3">
          <div className="d-flex align-items-center" style={{ fontWeight: 'bold', fontSize: '14px' }}>
              <HomeIcon width={18} height={18} className="text-primary mr-2" style={{ marginTop: '-1px' }} />
              {folderPath.map((pathItem, index) => (
                <React.Fragment key={pathItem.id || 'root'}>
                  {index === folderPath.length - 1 ? (
                    <span className="text-dark">{pathItem.name}</span>
                  ) : (
                    <>
                      <Button
                        variant="link"
                        className="p-0 text-decoration-none text-primary"
                        style={{ fontWeight: 'bold', fontSize: '14px' }}
                        onClick={() => {
                          const newPath = folderPath.slice(0, index + 1);
                          setFolderPath(newPath);
                          if (pathItem.isVirtual) {
                            setCurrentVirtualFolderId(pathItem.id);
                            setCurrentFolderId(null);
                          } else {
                            setCurrentFolderId(pathItem.id);
                            setCurrentVirtualFolderId(null);
                          }
                        }}
                      >
                        {pathItem.name}
                      </Button>
                      <span className="mx-2 text-muted">{'>'}</span>
                    </>
                  )}
                </React.Fragment>
              ))}
          </div>
        </div>

        {/* Desktop Table */}
        <div className={`${styles.scrollableContainer} non-mobile`}>
          <Row>
            <Col>
              {teamClausesData.length ? (
                <div>
                  <Table striped isStickyHeader isActionColumn paginationBelow isStickyPagination table={table} />
                </div>
              ) : (
                <Container className="mt-5">
                  <div className="lead text-center">
                    <h2>
                      <FormattedMessage id="title.empty-clauses" defaultMessage="No Clauses" />
                    </h2>
                    <FormattedMessage
                      id="phrase.templates.no-templates"
                      defaultMessage="Your team's clauses will appear here."
                    />
                  </div>
                </Container>
              )}
            </Col>
          </Row>
        </div>

        {/* Mobile Table */}
        <div className={styles.mobileTableContainer}>
          {teamClausesData.length ? (
            <>
              <div className={styles.mobileListArea}>
                <MobileTable>
                {table.page?.map((row: any) => {
                  const clause = row.original;
                  return (
                    <MobileTemplateItem
                      key={`mobile-clause-${clause.template.id}`}
                      template={clause}
                      isAdmin={isAdmin}
                      canEdit={canEdit}
                      teamId={teamId}
                      selectedTemplate={documentTemplatesData?.getDocumentTemplates?.find(
                        (tmp: any) => tmp.id === clause.template.id
                      )}
                      templateIsUpdated={clauseIsUpdated}
                      onClick={() => onClauseDetail(clause.template)}
                      context="clauses"
                      refetchFolders={refetchFolders}
                    />
                  );
                })}
                </MobileTable>
              </div>
              <MobilePagination
                pageIndex={table.state.pageIndex}
                pageCount={table.pageCount}
                canPreviousPage={table.canPreviousPage}
                canNextPage={table.canNextPage}
                gotoPage={table.gotoPage}
                previousPage={table.previousPage}
                nextPage={table.nextPage}
                totalItems={teamClausesData.length}
                pageSize={table.state.pageSize}
              />
            </>
          ) : (
            <Container className="mt-5">
              <div className="lead text-center">
                <h2>
                  <FormattedMessage id="title.empty-clauses" defaultMessage="No Clauses" />
                </h2>
                <FormattedMessage
                  id="phrase.templates.no-templates"
                  defaultMessage="Your team's clauses will appear here."
                />
              </div>
            </Container>
          )}
        </div>
      </ContentBody>
      <FileUploadModal
        onClose={() => setActiveModal(null)}
        isOpen={activeModal === ModalActionsEnum.UPLOAD_FILE}
        teamId={teamId}
        templateUpload
        isClauseUpload
        folders={allFoldersForCreation}
        selectedFolderId={selectedFolderForCreation}
        onFolderSelectionChange={setSelectedFolderForCreation}
        foldersLoading={isTemplateFoldersLoading}
      />
      <FilterSidebar
        isOpen={showFilter}
        filterStatuses={filterStatuses}
        selectedCreator={selectedCreator}
        onClose={() => setShowFilter(!showFilter)}
        setSorting={setSorting}
        setFilterStatuses={setFilterStatuses}
        setSelectedCreator={setSelectedCreator}
        allCreators={allCreators}
      />
      <CreateClauseFolderModal
        isOpen={showCreateFolderModal}
        onClose={() => setShowCreateFolderModal(false)}
        teamId={teamId}
        parentFolderId={currentFolderId || undefined}
        onFolderCreated={() => {
          setShowCreateFolderModal(false);
          refetch();
          refetchFolders();
        }}
      />
    </DropdownProvider>
  );
};

export default ClausesLandingPage;
